import { motion } from "framer-motion";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ProjectionResults, generateMonthlyProjections, AssumptionInputs } from "../../utils/projectionCalculations";
import { formatCurrency } from "../../utils/formatters";
import { Edit2 } from "lucide-react";
import { useState } from "react";

interface ProjectionChartsProps {
  projections: ProjectionResults;
  numberOfPods: number;
  assumptions: AssumptionInputs;
  setAssumptions: (assumptions: AssumptionInputs) => void;
}

const ProjectionCharts = ({ projections, numberOfPods, assumptions, setAssumptions }: ProjectionChartsProps) => {
  const [editMode, setEditMode] = useState(false);
  
  const monthlyData = generateMonthlyProjections(
    numberOfPods,
    projections.revenuePerPod / projections.utilizedHoursPerPod,
    (projections.utilizedHoursPerPod / assumptions.hoursPerMonth) * 100,
    assumptions
  );
  
  const handleAssumptionChange = (key: keyof AssumptionInputs, value: number) => {
    setAssumptions({
      ...assumptions,
      [key]: value,
    });
  };

  const pieData = [
    {
      name: "Revenue Share to Hosts",
      value: projections.revenueShareToHosts,
      color: "hsl(var(--muted))",
    },
    {
      name: "Operating Costs",
      value: projections.totalOperatingCosts - projections.revenueShareToHosts,
      color: "hsl(var(--accent))",
    },
    {
      name: "Fixed Overhead",
      value: projections.fixedOverhead,
      color: "hsl(var(--secondary))",
    },
    {
      name: "Net Profit",
      value: Math.max(0, projections.monthlyNetProfit),
      color: "hsl(var(--success))",
    },
  ].filter((item) => item.value > 0);

  return (
    <div className="grid desktop:grid-cols-2 laptop:grid-cols-2 tablet:grid-cols-1 mobile:grid-cols-1 gap-8">
      {/* Monthly Revenue Projection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-card rounded-2xl p-6 shadow-lg border border-border"
      >
        <h3 className="font-funnel font-semibold text-xl text-card-foreground mb-4">
          12-Month Revenue Projection
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Assumes gradual utilization ramp: 60% → 80% → 100% target
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="monthName"
              stroke="hsl(var(--muted-foreground))"
              style={{ fontSize: "12px" }}
            />
            <YAxis
              stroke="hsl(var(--muted-foreground))"
              style={{ fontSize: "12px" }}
              tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
              }}
              formatter={(value: number) => [formatCurrency(value), "Revenue"]}
            />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="hsl(var(--brand))"
              strokeWidth={3}
              dot={{ fill: "hsl(var(--brand))", r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Profit Margin Breakdown */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-card rounded-2xl p-6 shadow-lg border border-border"
      >
        <h3 className="font-funnel font-semibold text-xl text-card-foreground mb-4">
          Revenue Distribution
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Where your monthly revenue goes
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) =>
                `${name}: ${(percent * 100).toFixed(0)}%`
              }
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
              }}
              formatter={(value: number) => formatCurrency(value)}
            />
          </PieChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Monthly Profit Trend */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-card rounded-2xl p-6 shadow-lg border border-border"
      >
        <h3 className="font-funnel font-semibold text-xl text-card-foreground mb-4">
          Monthly Profit Trend
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Net profit as utilization ramps up
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="monthName"
              stroke="hsl(var(--muted-foreground))"
              style={{ fontSize: "12px" }}
            />
            <YAxis
              stroke="hsl(var(--muted-foreground))"
              style={{ fontSize: "12px" }}
              tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
              }}
              formatter={(value: number) => [formatCurrency(value), "Profit"]}
            />
            <Bar
              dataKey="profit"
              fill="hsl(var(--success))"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Assumptions Panel */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-muted/30 rounded-2xl p-6 border border-border"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-funnel font-semibold text-xl text-card-foreground">
            Key Assumptions
          </h3>
          <button
            onClick={() => setEditMode(!editMode)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            {editMode ? "Done" : "Edit"}
          </button>
        </div>
        <div className="space-y-3 text-sm">
          <EditableAssumptionRow 
            label="Hours per month" 
            value={assumptions.hoursPerMonth}
            onChange={(val) => handleAssumptionChange('hoursPerMonth', val)}
            editMode={editMode}
            suffix=" hours"
          />
          <EditableAssumptionRow 
            label="Revenue share to hosts" 
            value={assumptions.revenueSharePercent}
            onChange={(val) => handleAssumptionChange('revenueSharePercent', val)}
            editMode={editMode}
            suffix="%"
          />
          <EditableAssumptionRow 
            label="Maintenance per pod" 
            value={assumptions.maintenanceCostPerPod}
            onChange={(val) => handleAssumptionChange('maintenanceCostPerPod', val)}
            editMode={editMode}
            prefix="£"
            suffix="/month"
          />
          <EditableAssumptionRow 
            label="Insurance per pod" 
            value={assumptions.insuranceCostPerPod}
            onChange={(val) => handleAssumptionChange('insuranceCostPerPod', val)}
            editMode={editMode}
            prefix="£"
            suffix="/month"
          />
          <EditableAssumptionRow 
            label="Payment processing" 
            value={assumptions.paymentProcessingPercent}
            onChange={(val) => handleAssumptionChange('paymentProcessingPercent', val)}
            editMode={editMode}
            suffix="%"
            step={0.1}
          />
          <EditableAssumptionRow 
            label="Fixed overhead" 
            value={assumptions.fixedOverhead}
            onChange={(val) => handleAssumptionChange('fixedOverhead', val)}
            editMode={editMode}
            prefix="£"
            suffix="/month"
          />
          <EditableAssumptionRow 
            label="Initial pod cost" 
            value={assumptions.initialPodCost}
            onChange={(val) => handleAssumptionChange('initialPodCost', val)}
            editMode={editMode}
            prefix="£"
          />
        </div>
      </motion.div>
    </div>
  );
};

const EditableAssumptionRow = ({ 
  label, 
  value, 
  onChange, 
  editMode, 
  prefix = "", 
  suffix = "",
  step = 1
}: { 
  label: string; 
  value: number; 
  onChange: (value: number) => void;
  editMode: boolean;
  prefix?: string;
  suffix?: string;
  step?: number;
}) => {
  return (
    <div className="flex justify-between items-center py-2 border-b border-border/50 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      {editMode ? (
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          step={step}
          className="w-32 px-3 py-1 text-right font-medium text-card-foreground bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
        />
      ) : (
        <span className="font-medium text-card-foreground">
          {prefix}{value.toLocaleString()}{suffix}
        </span>
      )}
    </div>
  );
};

export default ProjectionCharts;
